import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../utils/apiClient';

const HEARTBEAT_MS = 15_000;
const IDLE_AFTER_MS = 60_000;

const PAGE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/calendar': 'Kalender',
  '/my-bookings': 'Meine Buchungen',
  '/my-drinks': 'Meine Verzehrkosten',
  '/my-dogs': 'Meine Hunde',
  '/profile': 'Mein Profil',
  '/my-projects': 'Vereinsprojekte',
  '/veranstaltungen': 'Veranstaltungen',
  '/members': 'Mitgliederverwaltung',
  '/dog-management-beta': 'Hundeverwaltung',
  '/guest-cards': 'Gästekarten',
  '/verzehrkosten': 'Verzehrkosten-Verwaltung',
  '/account-overview': 'Finanzübersicht',
  '/account-statements': 'Kontoauszüge',
  '/budget-planning': 'Budgetplanung',
  '/paypal-payments': 'PayPal-Zahlungen',
  '/documents': 'Dokumente',
  '/member-statistics': 'Mitgliederstatistik',
  '/ki-nutzung': 'KI-Nutzung',
  '/admin/user-activity': 'Nutzungsanalyse',
  '/admin/config': 'Einstellungen',
  '/admin/structure': 'Vereinsstruktur',
  '/admin/kassenpruefer': 'Kassenprüfer-Verwaltung',
  '/admin/email-protokoll': 'E-Mail-Protokoll',
  '/admin/backup-export': 'Backup & Export',
  '/admin/backup-status': 'Backup-Status',
  '/admin/events': 'Veranstaltungsverwaltung',
};

const createSessionId = () => {
  const existing = sessionStorage.getItem('user_activity_session_id');
  if (existing) return existing;

  const id = typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem('user_activity_session_id', id);
  return id;
};

const pageLabel = (path: string) => PAGE_LABELS[path] || path;

/**
 * Zaehlt nur Sekunden, in denen der Tab sichtbar ist und der Benutzer in der
 * letzten Minute interagiert hat. Es werden keinerlei Interaktionsinhalte
 * aufgezeichnet.
 */
const UserActivityTracker: React.FC = () => {
  const location = useLocation();
  const sessionId = useRef(createSessionId());
  const currentPath = useRef(location.pathname);
  const pendingSeconds = useRef(0);
  const lastInteractionAt = useRef(Date.now());

  const flush = useCallback((keepalive = false) => {
    const seconds = pendingSeconds.current;
    if (seconds < 1) return;

    pendingSeconds.current = 0;
    const payload = {
      session_id: sessionId.current,
      page_path: currentPath.current,
      page_title: pageLabel(currentPath.current),
      active_seconds: Math.min(seconds, 60),
    };

    if (keepalive) {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
      void fetch(`${baseUrl.replace(/\/$/, '')}/api/user-activity/heartbeat`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      }).catch(() => {});
      return;
    }

    void apiClient.post('/api/user-activity/heartbeat', payload).catch(() => {
      // Tracking darf die eigentliche Portalnutzung niemals stoeren.
    });
  }, []);

  useEffect(() => {
    if (location.pathname === currentPath.current) return;
    flush();
    currentPath.current = location.pathname;
    lastInteractionAt.current = Date.now();
  }, [flush, location.pathname]);

  useEffect(() => {
    const markInteraction = () => {
      lastInteractionAt.current = Date.now();
    };
    const tick = () => {
      const active = document.visibilityState === 'visible'
        && Date.now() - lastInteractionAt.current <= IDLE_AFTER_MS;
      if (active) pendingSeconds.current += 1;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush(true);
      else markInteraction();
    };
    const onPageHide = () => flush(true);

    const interactionEvents: Array<keyof WindowEventMap> = [
      'pointerdown', 'keydown', 'scroll', 'touchstart',
    ];
    interactionEvents.forEach((event) => window.addEventListener(event, markInteraction, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    const secondTimer = window.setInterval(tick, 1_000);
    const heartbeatTimer = window.setInterval(() => flush(), HEARTBEAT_MS);

    return () => {
      window.clearInterval(secondTimer);
      window.clearInterval(heartbeatTimer);
      interactionEvents.forEach((event) => window.removeEventListener(event, markInteraction));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      flush(true);
    };
  }, [flush]);

  return null;
};

export default UserActivityTracker;
